/*
 * -----------------------------------------------------------------------
 *  <copyright file="admin/organisations/indications/index.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { orderBy } from '@progress/kendo-data-query';
import { useBoolean, useTranslation, useAuthorization } from 'hooks';
import { OrganisationIndicationFields } from 'queries/generated/fragments.generated';
import { GET_ORGANISATION_PROGRAM_PRODUCT } from 'queries/uam';
import NotificationService from 'utils/notification';
import EditIndication from 'views/components/admin/organisations/edit-indication';
import IndicationsGrid from 'views/components/admin/organisations/indications-grid';
import Button from 'views/components/core/buttons/button';
import Loading from 'views/components/core/loading';
import PageTitle from 'views/components/core/page-title/page-title';
import Error from 'views/components/error/error';

import 'views/App.scss';

const AdminOrganisationIndications = () => {
    const { t } = useTranslation();
    const user = useAuthorization();
    const canEdit = user?.canWriteSystemAdminLevel();

    const [loadData, { data, error, loading }] = useLazyQuery(GET_ORGANISATION_PROGRAM_PRODUCT);

    const [currentIndication, setCurrentIndication] = useState(null);
    const showEditIndication = useBoolean(false);

    useEffect(() => {
        loadData();
    }, [user]);

    if (loading) return <Loading />;

    if (error) return <Error error={error} />;

    const indications = data
        ? orderBy(data?.organisation?.indications, [{ field: 'name', dir: 'asc' }]).map((item: any) => {
              const amountStudies =
                  data?.organisation.studies.filter((el: any) => el.indicationsIds.indexOf(item.id) >= 0)?.length ?? 0;
              const amountPrograms =
                  data?.organisation.programs.filter((el: any) => el.indicationIds.indexOf(item.id) >= 0)?.length ?? 0;
              return {
                  ...item,
                  amountStudies,
                  amountPrograms,
              };
          })
        : [];

    return (
        <>
            <EditIndication
                indication={currentIndication}
                indications={indications}
                open={showEditIndication.value}
                onClose={(action?: string) => {
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else {
                        if (action === 'add')
                            NotificationService.showNotification(t('admin:indicationSuccessfulCreated'));
                        else if (action === 'edit')
                            NotificationService.showNotification(
                                t('admin:indicationSuccessfulUpdated', {
                                    id: currentIndication?.recordId ?? currentIndication.id,
                                }),
                            );
                        setCurrentIndication(null);
                        showEditIndication.setFalse();
                    }
                }}
            />
            <PageTitle text={t('admin:manageIndications')} />
            <br />
            <Button
                text="admin:addIndication"
                onClick={() => {
                    showEditIndication.setTrue();
                }}
                className="main-button"
                disabled={!canEdit}
                style={{ marginLeft: 'auto', display: 'flex', marginTop: 20, marginBottom: 20 }}
            />
            <IndicationsGrid
                indications={indications}
                rowActions={(indication: OrganisationIndicationFields) => [
                    {
                        label: 'admin:editIndication',
                        icon: ['fal', 'edit'],
                        enabled: canEdit,
                        onClick: () => {
                            setCurrentIndication(indication);
                            showEditIndication.setTrue();
                        },
                    },
                ]}
            />
        </>
    );
};

export default AdminOrganisationIndications;
